/**
 * The live-data socket.
 *
 * A trading terminal is left open for hours on a laptop that sleeps, changes
 * network and loses signal in lifts. So the interesting part of this file is
 * not sending frames — it is what happens after the connection drops:
 *
 *   - reconnect with capped exponential backoff and jitter, so ten thousand
 *     browsers coming back after a deploy do not arrive in the same second
 *   - re-authenticate and re-subscribe automatically, because the server
 *     remembers nothing about a socket that closed
 *   - never queue price ticks while disconnected; the latest price is the only
 *     one worth having, and a backlog of stale quotes is worse than a gap
 *
 * A client that reconnects but forgets what it was watching looks exactly like
 * a frozen chart, which is the bug this file mostly exists to prevent.
 */

import { api } from './api'

export type ServerEvent =
  | 'welcome'
  | 'subscribed'
  | 'unsubscribed'
  | 'authorized'
  | 'pong'
  | 'error'
  | 'tick'
  | 'candle'
  | 'trade.opened'
  | 'trade.closed'
  | 'balance'

export interface ServerMessage<T = unknown> {
  event: ServerEvent
  id?: string
  topic?: string
  data?: T
  error?: { code: string; message: string }
  at: string
}

export interface Tick {
  symbol: string
  price: string
  bid?: string
  ask?: string
  at: string
}

export interface Candle {
  symbol: string
  interval: string
  open: string
  high: string
  low: string
  close: string
  volume: string
  open_time: string
  closed: boolean
}

export interface TradeUpdate {
  trade_id: string
  symbol: string
  direction: string
  status: string
  outcome?: string
  stake: string
  payout?: string
  profit_loss?: string
  strike_price?: string
  close_price?: string
  expires_at?: string
  settled_at?: string
}

export interface BalanceUpdate {
  wallet_id: string
  kind: string
  currency: string
  balance: string
  delta: string
  reason?: string
}

type Handler<T = any> = (data: T, message: ServerMessage<T>) => void

/** A subscription the client restores after every reconnect. */
interface Subscription {
  channel: string
  symbol?: string
  interval?: string
}

export type ConnectionState = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed'

export class MarketSocket {
  private ws: WebSocket | null = null
  private state: ConnectionState = 'idle'
  private attempt = 0
  private reconnectTimer: number | null = null
  private closedByUs = false

  /** Everything to restore on reconnect, keyed so re-subscribing is idempotent. */
  private subscriptions = new Map<string, Subscription>()
  private handlers = new Map<string, Set<Handler>>()
  private stateHandlers = new Set<(s: ConnectionState) => void>()

  constructor(private readonly url = defaultURL()) {
    // Signing in or out mid-session re-authenticates the live socket rather
    // than forcing a reconnect: the public subscriptions stay up throughout.
    api.onTokenChange((tokens) => {
      if (this.state !== 'open') return
      if (tokens) this.send({ op: 'authenticate', id: 'auth', token: tokens.access_token })
      else this.reconnect(0)
    })
  }

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  connect(): void {
    if (this.ws && (this.state === 'open' || this.state === 'connecting')) return
    this.closedByUs = false
    this.setState(this.attempt === 0 ? 'connecting' : 'reconnecting')

    try {
      this.ws = new WebSocket(this.url)
    } catch {
      this.scheduleReconnect()
      return
    }

    this.ws.onopen = () => {
      this.attempt = 0
      this.setState('open')

      // The server remembers nothing about a socket that closed, so identity
      // and subscriptions are both re-established here rather than assumed.
      const token = api.accessToken
      if (token) this.send({ op: 'authenticate', id: 'auth', token })
      for (const sub of this.subscriptions.values()) {
        this.send({ op: 'subscribe', ...sub })
      }
    }

    this.ws.onmessage = (event) => {
      let message: ServerMessage
      try {
        message = JSON.parse(event.data as string)
      } catch {
        return
      }
      this.dispatch(message)
    }

    this.ws.onclose = () => {
      this.ws = null
      if (this.closedByUs) {
        this.setState('closed')
        return
      }
      this.scheduleReconnect()
    }

    this.ws.onerror = () => {
      // onclose always follows, and it owns the reconnect. Doing anything
      // here would schedule two.
    }
  }

  disconnect(): void {
    this.closedByUs = true
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.ws?.close(1000, 'client disconnect')
    this.ws = null
    this.setState('closed')
  }

  private reconnect(delay: number): void {
    this.ws?.close()
    this.ws = null
    this.reconnectTimer = window.setTimeout(() => this.connect(), delay)
  }

  /**
   * Backoff with jitter.
   *
   * The jitter is the point. Without it, every browser disconnected by a
   * deploy retries on the same schedule and arrives together, which turns one
   * restart into a thundering herd against a service that has just started.
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return
    this.setState('reconnecting')

    const base = Math.min(1000 * 2 ** this.attempt, 30_000)
    const delay = base * (0.5 + Math.random() * 0.5)
    this.attempt++

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null
      this.connect()
    }, delay)
  }

  // -------------------------------------------------------------------------
  // Subscriptions
  // -------------------------------------------------------------------------

  subscribePrices(symbol: string): () => void {
    return this.subscribe({ channel: 'prices', symbol })
  }

  subscribeCandles(symbol: string, interval: string): () => void {
    return this.subscribe({ channel: 'candles', symbol, interval })
  }

  /** Private channels; the server derives the topic from the token. */
  subscribeTrades(): () => void {
    return this.subscribe({ channel: 'trades' })
  }

  subscribeBalance(): () => void {
    return this.subscribe({ channel: 'balance' })
  }

  private subscribe(sub: Subscription): () => void {
    const key = subscriptionKey(sub)
    if (!this.subscriptions.has(key)) {
      this.subscriptions.set(key, sub)
      if (this.state === 'open') this.send({ op: 'subscribe', ...sub })
    }
    return () => this.unsubscribe(sub)
  }

  private unsubscribe(sub: Subscription): void {
    const key = subscriptionKey(sub)
    if (!this.subscriptions.delete(key)) return
    if (this.state === 'open') this.send({ op: 'unsubscribe', ...sub })
  }

  // -------------------------------------------------------------------------
  // Events
  // -------------------------------------------------------------------------

  on<T = unknown>(event: ServerEvent, handler: Handler<T>): () => void {
    let set = this.handlers.get(event)
    if (!set) {
      set = new Set()
      this.handlers.set(event, set)
    }
    set.add(handler as Handler)
    return () => set!.delete(handler as Handler)
  }

  onStateChange(handler: (s: ConnectionState) => void): () => void {
    this.stateHandlers.add(handler)
    handler(this.state)
    return () => this.stateHandlers.delete(handler)
  }

  get connectionState(): ConnectionState {
    return this.state
  }

  private dispatch(message: ServerMessage): void {
    const set = this.handlers.get(message.event)
    if (!set) return
    for (const handler of set) {
      try {
        handler(message.data, message)
      } catch (err) {
        // One broken handler must not stop the others, and must not kill the
        // socket's message loop.
        console.error('[socket] handler threw', err)
      }
    }
  }

  private setState(state: ConnectionState): void {
    if (this.state === state) return
    this.state = state
    this.stateHandlers.forEach((fn) => fn(state))
  }

  private send(payload: Record<string, unknown>): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return
    this.ws.send(JSON.stringify(payload))
  }
}

function subscriptionKey(sub: Subscription): string {
  return [sub.channel, sub.symbol ?? '', sub.interval ?? ''].join(':')
}

function defaultURL(): string {
  // Follow the API origin when one is configured, so the socket does not stay
  // behind on the page's own host after the API moves to its own name. The
  // scheme has to move with it: an https API means wss, and a browser refuses
  // a ws:// socket opened from an https page.
  const base = import.meta.env.VITE_API_BASE_URL
  if (base) {
    const api = new URL(base, location.href)
    return `${api.protocol === 'https:' ? 'wss:' : 'ws:'}//${api.host}/ws`
  }

  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${location.host}/ws`
}

export const socket = new MarketSocket()
